/**
 * Market. Two storefronts on one chassis, and they are two different economies.
 *
 * **The Fence** is the player order book. Clean cash only, every listing escrowed, and the seller
 * pays the Heat-scaled fence fee at the moment of sale, so cashing out costs more the harder you
 * have been earning.
 *
 * **The Black Market** is the house. Dirty cash only, priced above book, rotating hourly. It is
 * where dirty money buys gear and it is deliberately a bad deal: the route back out runs through
 * the fence fee and the laundering haircut, so the round trip always loses.
 *
 * Contraband appears in neither. It converts to dirty cash at the NPC fence on the Money screen
 * and nowhere else, which is what keeps the dirty tank closed.
 *
 * NOTHING ON THIS SCREEN IS COMPUTED HERE. Every price, fee, floor and yield arrives priced
 * against this player at this instant, from the same functions the commit uses. The client
 * renders numbers and sends gestures; the server decides.
 */

import { useEffect, useMemo, useState } from 'react'
import type {
  BlackMarketOffer,
  CatalogItem,
  Cents,
  InventoryItem,
  Listing,
  MarketAisle,
  MarketCategory,
} from '@ashport/shared'
import { A, nth } from '../art'
import { Icon, Res, type IconName } from '../ui/Icon'
import { Art, Btn, Col, Meter, Panel, PanelHead, Rail, Row, Spacer, Stack, Tabs } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import { CUR, gearOf, ItemCard } from '../ui/ItemCard'
import { useSession } from '../game/session'
import { clock, exact, money, useGame, type Rarity } from '../game/state'

/* ------------------------------------------------------------------- types */

type Mode = 'player' | 'black'
type Cat = MarketAisle

const CATS: { id: Cat; label: string; icon: IconName }[] = [
  { id: 'all', label: 'All', icon: 'market' },
  { id: 'weapons', label: 'Weapons', icon: 'target' },
  { id: 'vehicles', label: 'Vehicles', icon: 'bolt' },
  { id: 'armor', label: 'Armor', icon: 'legend' },
  { id: 'business', label: 'Businesses', icon: 'jobs' },
  { id: 'contraband', label: 'Contraband', icon: 'crate' },
  { id: 'materials', label: 'Materials', icon: 'territory' },
  { id: 'services', label: 'Services', icon: 'users' },
]

/*
 * `shardFor` WAS HERE and it read `cat === 'all' ? 'weapons' : cat`, under a comment saying "`all`
 * is a filter over one shard, never a fan-out".
 *
 * It was not a filter. Nothing filtered: the weapons board was rendered verbatim under the word
 * All, with the whole catalogue's count of 135 printed over it and the player's listings in the
 * other six aisles absent from "Listed (n)". `GET /api/market/all` now reads all seven books and
 * merges them, so the aisle the rail says is the aisle the board is, and this function is gone
 * rather than corrected: there is no translation left to do.
 */

/** The button tone per currency; the colours and labels live on the card now. */
const BTN: Record<'dirty' | 'clean', 'safe' | 'police'> = { dirty: 'safe', clean: 'police' }

/**
 * THE VIEWER'S WORN POWER PER SLOT, so a card can say what changes. Phase 21.
 *
 * From the market's own inventory (which carries `equipped`) joined to the catalogue (which
 * carries `power`), so no extra read. `null` for a slot with nothing in it, which the card
 * prints as "slot empty"; `undefined` when the inventory has not arrived, which it prints as
 * nothing at all.
 */
function wornBySlot(
  inventory: InventoryItem[] | undefined,
  catalog: Map<string, CatalogItem>,
): Record<'weapons' | 'armor' | 'vehicles', { power: number; name: string } | null> | undefined {
  if (!inventory) return undefined
  const out: Record<'weapons' | 'armor' | 'vehicles', { power: number; name: string } | null> = {
    weapons: null,
    armor: null,
    vehicles: null,
  }
  for (const i of inventory) {
    if (!i.equipped) continue
    if (i.category !== 'weapons' && i.category !== 'armor' && i.category !== 'vehicles') continue
    const def = gearOf(catalog.get(i.itemId))
    out[i.category] = { power: def?.power ?? 0, name: i.name }
  }
  return out
}
type Worn = ReturnType<typeof wornBySlot>
const wornFor = (worn: Worn, def: CatalogItem | undefined) => {
  const g = gearOf(def)
  return g && worn ? worn[g.category] : undefined
}

const ago = (ms: number, now: number): string => {
  const s = Math.max(0, (now - ms) / 1000)
  if (s < 90) return 'just now'
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  if (s < 86_400) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86_400)}d ago`
}

/* ------------------------------------------------------------------ pieces */

function Spark({ d, c, h = 34 }: { d: number[]; c: string; h?: number }) {
  const w = 100
  const lo = Math.min(...d)
  const hi = Math.max(...d)
  const span = hi - lo || 1
  const y = (v: number) => h - 2 - ((v - lo) / span) * (h - 4)
  const pts = d.map((v, i) => `${(i / (d.length - 1)) * w},${y(v)}`).join(' ')
  return (
    <svg
      width="100%"
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
      aria-hidden
    >
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={c} fillOpacity={0.1} />
      <polyline points={pts} fill="none" stroke={c} strokeWidth={1.3} vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

const selectStyle: React.CSSProperties = {
  appearance: 'none',
  padding: '7px 24px 7px 9px',
  background: '#05080a',
  border: '1px solid var(--line)',
  color: 'var(--ink)',
  font: '400 11px/1 var(--f-head)',
  letterSpacing: '.1em',
  textTransform: 'uppercase',
  outline: 'none',
  cursor: 'pointer',
  minWidth: 0,
  width: '100%',
}

function Select({
  label,
  value,
  onChange,
  opts,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  opts: { v: string; l: string }[]
}) {
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
      <span
        className="t-label"
        style={{ position: 'absolute', left: 9, fontSize: sz(9), color: 'var(--ink-dim)', pointerEvents: 'none' }}
      >
        {label}:
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...selectStyle, paddingLeft: 12 + label.length * 6.2 }}
      >
        {opts.map((o) => (
          <option key={o.v} value={o.v} style={{ background: '#05080a', color: 'var(--ink)' }}>
            {o.l}
          </option>
        ))}
      </select>
      <Icon n="chevd" s={11} c="var(--ink-dim)" style={{ position: 'absolute', right: 8, pointerEvents: 'none' }} />
    </div>
  )
}

function CatRail({
  cat,
  setCat,
  counts,
  stacked,
}: {
  cat: Cat
  setCat: (c: Cat) => void
  counts: Record<string, number>
  stacked: boolean
}) {
  return (
    <Panel flat style={{ flex: 'none' }}>
      {/* Eight aisles in eight equal columns, or on a narrow screen a strip that scrolls sideways. */}
      <div
        className={stacked ? 'scroll-x' : undefined}
        style={
          stacked
            ? { display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'minmax(104px, 1fr)' }
            : { display: 'grid', gridTemplateColumns: `repeat(${CATS.length}, minmax(0, 1fr))` }
        }
      >
        {/* Services has no catalogue behind it yet (DESIGN-DELTAS 1w.10); a cell reading "0 in
            the catalogue" is a door painted on a wall. It appears the day something is behind it. */}
        {CATS.filter((c) => c.id !== 'services' || (counts.services ?? 0) > 0).map((c, i) => {
          const on = cat === c.id
          return (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                padding: '9px 4px 8px',
                background: on
                  ? 'linear-gradient(180deg, rgba(214,168,75,.16), rgba(214,168,75,.02))'
                  : 'transparent',
                border: 'none',
                borderLeft: i === 0 ? 'none' : '1px solid var(--line-soft)',
                boxShadow: on ? 'inset 0 -2px 0 var(--gold)' : undefined,
                color: on ? 'var(--gold)' : 'var(--ink-mid)',
                cursor: 'pointer',
                transition: 'color 130ms ease, background 130ms ease',
              }}
            >
              <Icon n={c.icon} s={20} c={on ? 'var(--gold)' : 'var(--ink-dim)'} />
              <span className="t-head" style={{ fontSize: sz(10), letterSpacing: '.15em' }}>
                {c.label}
              </span>
              {/* The CATALOGUE count, and it says so. It is how much variety exists, not how
                  much is for sale: "31 in the book" under a rail read as "31 are listed", which
                  is the kind of number that is wrong in a way that renders beautifully. */}
              <span className="t-mono" style={{ fontSize: sz(9.5), color: on ? 'var(--brass-lit)' : 'var(--ink-faint)' }}>
                {counts[c.id] ?? 0} in the catalogue
              </span>
            </button>
          )
        })}
      </div>
    </Panel>
  )
}

/** A player listing. Everything on it came off the wire; nothing is derived here. */
function ListingCard({
  it,
  def,
  mine,
  busy,
  worn,
  onBuy,
  onCancel,
}: {
  it: Listing
  def: CatalogItem | undefined
  mine: boolean
  busy: boolean
  worn: Worn
  onBuy: () => void
  onCancel: () => void
}) {
  // A sparkline of nothing is a chart with no data in it, which reads as a chart at zero.
  const hasTrend = it.trend.some((v) => v > 0)
  const rc = `var(--r-${it.rarity === 'legendary' ? 'legend' : it.rarity})`
  return (
    <div style={{ display: 'flex', minWidth: 0 }}>
      <Panel style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <div style={{ padding: '8px 9px 10px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <ItemCard
            def={def}
            name={it.name}
            sub={it.sub}
            kind={it.kind}
            rarity={it.rarity}
            artId={it.artId}
            price={it.price}
            cur={it.cur}
            baseValue={def?.baseValue}
            cond={it.cond}
            qty={it.qty}
            worn={wornFor(worn, def)}
            flag={
              mine ? (
                <span className="t-label" style={{ fontSize: sz(8.5), color: 'var(--gold)' }}>
                  Yours
                </span>
              ) : undefined
            }
          >
            {hasTrend && (
              <>
                <div className="t-label" style={{ fontSize: sz(8.5), marginTop: 8 }}>
                  Price Trend
                </div>
                <div style={{ marginTop: 3, borderBottom: '1px solid var(--line-soft)' }}>
                  <Spark d={it.trend} c={rc} h={34} />
                </div>
              </>
            )}
            <Row gap={5} style={{ marginTop: 7 }}>
              <span className="t-label" style={{ fontSize: sz(8.5) }}>Seller</span>
              <span
                style={{
                  fontSize: sz(10.5),
                  color: 'var(--ink)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {it.seller}
              </span>
              {it.verified && <Icon n="crown" s={10} c="var(--gold)" />}
            </Row>
            <Spacer />
            <div style={{ marginTop: 8 }}>
              {mine ? (
                <Btn v="ghost" size="sm" full disabled={busy} onClick={onCancel}>
                  Pull It
                </Btn>
              ) : (
                <Btn v={BTN[it.cur]} size="sm" full disabled={busy} onClick={onBuy}>
                  Buy
                </Btn>
              )}
            </div>
          </ItemCard>
        </div>
      </Panel>
    </div>
  )
}

/**
 * One item the house is holding this hour. Dirty cash, above book, no receipts.
 *
 * IT SHOWS THE ITEM NOW. This card printed rarity, name, line, book, stock and a price, and not
 * one of the three stats, the power, the Condition or a business's income, while the Fence card
 * two panels to the left printed all of them. The audit that opened Phase 18 asked "items have no
 * stats?" off a screenshot of this card. Same card as the Fence now, plus the shelf count.
 */
function BlackCard({
  o,
  def,
  worn,
  busy,
  onBuy,
}: {
  o: BlackMarketOffer
  def: CatalogItem | undefined
  worn: Worn
  busy: boolean
  onBuy: () => void
}) {
  return (
    <Panel flat style={{ flex: 1, minWidth: 0 }}>
      <div style={{ padding: '8px 9px 9px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <ItemCard
          def={def}
          name={o.name}
          sub={o.sub}
          kind={def?.category === 'business' ? 'Front' : o.category}
          rarity={o.rarity}
          artId={o.artId}
          price={o.price}
          cur="dirty"
          baseValue={o.baseValue}
          worn={wornFor(worn, def)}
          flag={
            <span className="t-mono" style={{ fontSize: sz(9.5), color: 'var(--ink-faint)' }}>
              {o.stock} on the shelf
            </span>
          }
        >
          <div style={{ marginTop: 9 }}>
            <Btn v="safe" size="sm" full disabled={busy} onClick={onBuy}>
              Take It
            </Btn>
          </div>
        </ItemCard>
      </div>
    </Panel>
  )
}

/* -------------------------------------------------------------------- screen */

/**
 * THE KIT: equip, repair and crush, none of which a player could reach.
 *
 * `GET /api/gear` computes 26 fields per row on every read: what is worn, its Condition, the cost
 * to repair it, what the Crusher would pay and in what material. `equip`, `repair` and `crush` have
 * all been on the store since Phase 5 and `POST /api/gear/equip`, `/repair` and `/crush` have all
 * been on the server just as long. **No component called any of them.**
 *
 * So a player could buy a weapon on the board two panels to the left of this one and never wear
 * it. Condition decayed with no remedy. The Crusher, which is the floor value under every low tier
 * item and the whole reason a Blackjack is worth picking up, was unreachable. That is the same
 * shape as contraband having no exit: not a missing feature, a promise the shop makes and the game
 * does not keep.
 *
 * It lives on the Market rather than on a screen of its own because this is where the things were
 * bought. A player who has just spent money on a coat should not have to find another page to put
 * it on.
 *
 * Every number here is the server's. Condition, repair cost, crusher payout and the item's Power
 * are all computed against this player at this instant, and the cost is on the button before the
 * tap.
 */
function KitTab() {
  const gear = useSession((s) => s.gear)
  const busy = useSession((s) => s.busy)
  const equip = useSession((s) => s.equip)
  const repair = useSession((s) => s.repair)
  const crush = useSession((s) => s.crush)
  const loadGear = useSession((s) => s.loadGear)
  const catalog = useSession((s) => s.catalog)

  useEffect(() => {
    void loadGear()
  }, [loadGear])

  // Fronts are on their own tab now; the kit is what can be worn, patched or crushed.
  const rows = (gear?.items ?? []).filter((g) => g.category !== 'business')
  if (rows.length === 0) {
    return (
      <div style={{ fontSize: 11, color: 'var(--ink-faint)', padding: '10px 2px', lineHeight: 1.45 }}>
        Nothing in the kit yet. Weapons, coats and cars bought on the board turn up here to be worn,
        patched up or put under the crusher.
      </div>
    )
  }

  return (
    <>
      {rows.map((g) => (
        <div key={g.slotId} style={{ padding: '7px 2px 9px', borderBottom: '1px solid var(--line-soft)' }}>
          {/* The same card as the board, without the art: the kit is a list, and the picture is
              on the board where it is bought. Condition is stated as a number and not only as a
              bar, because the repair price is a function of how far it has fallen. */}
          <ItemCard
            def={catalog.get(g.itemId)}
            name={g.name}
            sub={g.sub}
            rarity={g.rarity as Rarity}
            artId={g.artId}
            cond={g.cond}
            qty={g.qty}
            equipped={g.equipped}
            compact
          />

          <Row gap={6} style={{ marginTop: 6, flexWrap: 'wrap' }}>
            {!g.equipped && g.power > 0 && (
              <Btn v="gold" size="sm" disabled={busy} onClick={() => void equip(g.slotId, g.itemId)}>
                Wear It
              </Btn>
            )}
            {g.cond !== null && g.cond < 100 && g.repairPoints > 0 && (
              <Btn
                v="ghost"
                size="sm"
                disabled={busy}
                onClick={() => void repair(g.slotId, g.repairPoints)}
              >
                {`Patch ${g.repairPoints} for ${exact(g.repairCost)}`}
              </Btn>
            )}
            {/*
              * The Crusher names what it pays and in WHAT, because "8 percent of base value" is a
              * rule and "two lengths of copper pipe" is a decision.
              */}
            {!g.equipped && g.crusherItemName !== null && (
              <Btn
                v="ghost"
                size="sm"
                disabled={busy}
                onClick={() => void crush(g.slotId, g.itemId, 1)}
              >
                {`Crush for ${g.crusherQty} ${g.crusherItemName}`}
              </Btn>
            )}
          </Row>
        </div>
      ))}
    </>
  )
}

/**
 * THE FRONTS, EACH ON ITS OWN. Phase 21.
 *
 * The game had twenty-four income properties and no screen for them: the shop card omitted the
 * income, the one Home card rendered only after a front was owned, and the till was a single
 * aggregate. This is a row per building with its own till, its hours and what it nets a day, the
 * cap and the collect over all of them, because collecting early destroys nothing (1n.3).
 */
function FrontsTab() {
  const gear = useSession((s) => s.gear)
  const busy = useSession((s) => s.busy)
  const collect = useSession((s) => s.collectFronts)
  const upgrade = useSession((s) => s.upgradeFront)
  const clean = useGame().player.clean
  const loadGear = useSession((s) => s.loadGear)
  const catalog = useSession((s) => s.catalog)
  useEffect(() => {
    void loadGear()
  }, [loadGear])
  const rows = gear?.frontRows ?? []
  if (!gear || rows.length === 0) {
    return (
      <div style={{ fontSize: 11, color: 'var(--ink-faint)', padding: '10px 2px', lineHeight: 1.45 }}>
        No fronts yet. A front is bought on the board like anything else, pays a small clean income
        into its till every hour, and is the only way to Run Legit. You may own {gear?.ownCap ?? 4}.
      </div>
    )
  }
  return (
    <>
      <Row gap={8} style={{ padding: '6px 2px 8px', borderBottom: '1px solid var(--line-soft)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-label" style={{ fontSize: sz(9) }}>In the tills</div>
          <div className="t-num" style={{ fontSize: 20, color: gear.tillCapped ? 'var(--heat)' : 'var(--clean)' }}>
            {exact(gear.till)}
          </div>
          <div style={{ fontSize: sz(10.5), color: gear.tillCapped ? 'var(--heat)' : 'var(--ink-faint)' }}>
            {gear.tillCapped
              ? `A till is full. It stopped at ${gear.accrualCapHours} hours.`
              : `${gear.fronts} of ${gear.ownCap} fronts. A till stops at ${gear.accrualCapHours} hours.`}
          </div>
        </div>
        <Btn v={gear.tillCapped ? 'gold' : 'safe'} size="sm" disabled={busy || gear.till <= 0} onClick={() => void collect()}>
          {gear.till > 0 ? 'Empty Them' : 'Nothing yet'}
        </Btn>
      </Row>
      {rows.map((f) => {
        const def = catalog.get(f.itemId)
        return (
          <div key={f.slotId} style={{ padding: '7px 2px 9px', borderBottom: '1px solid var(--line-soft)' }}>
            <Row gap={8} style={{ alignItems: 'center' }}>
              <Art
                src={A(f.artId)}
                alt={f.name}
                scrim="none"
                style={{ width: 38, height: 32, flex: 'none', border: '1px solid var(--line-soft)' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--ink-hi)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {f.name}
                  {f.qty > 1 ? ` ×${f.qty}` : ''}
                  {/* PHASE 22. The level, as a fact about the building. Level 1 is every deed that predates levels. */}
                  <span className="t-mono" style={{ marginLeft: 7, fontSize: sz(9.5), color: f.level >= gear.frontLevelCap ? 'var(--gold)' : 'var(--ink-dim)' }}>
                    Lv {f.level}
                    {f.level >= gear.frontLevelCap ? ', the top' : ` of ${gear.frontLevelCap}`}
                  </span>
                </div>
                <div style={{ fontSize: sz(10.5), color: 'var(--ink-faint)' }}>
                  {exact(f.netDaily)} a day
                  {def && def.category === 'business' && def.upkeepDaily > 0 ? `, ${exact(def.upkeepDaily)} of it upkeep` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right', flex: 'none' }}>
                <div className="t-num" style={{ fontSize: 13, color: f.capped ? 'var(--heat)' : 'var(--clean)' }}>
                  {exact(f.pending)}
                </div>
                <div className="t-mono" style={{ fontSize: sz(9.5), color: f.capped ? 'var(--heat)' : 'var(--ink-dim)' }}>
                  {f.capped ? 'full' : `${f.hours}h in`}
                </div>
              </div>
            </Row>
            <div style={{ marginTop: 5 }}>
              <Meter
                v={f.hours}
                max={gear.accrualCapHours}
                a="var(--brass-deep)"
                b={f.capped ? 'var(--heat)' : 'var(--clean)'}
                h={4}
              />
            </div>
            {/*
              * PHASE 22. THE NEXT LEVEL, PRICED BY THE SERVER against this row: clean off the
              * front's own net, materials of its own tier, and what that buys per day. The
              * button says which half is short rather than greying out without a word.
              */}
            {f.upgrade && (
              <Row gap={8} style={{ marginTop: 7, alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: sz(10.5), color: 'var(--ink-dim)', lineHeight: 1.4 }}>
                  Level {f.upgrade.toLevel}: <span style={{ color: clean >= f.upgrade.cost ? 'var(--clean)' : 'var(--arrest)' }}>{exact(f.upgrade.cost)}</span> clean and{' '}
                  <span style={{ color: f.upgrade.materialHave >= f.upgrade.materialQty ? 'var(--ink-hi)' : 'var(--arrest)' }}>
                    {f.upgrade.materialQty} {f.upgrade.materialName}
                    {f.upgrade.materialQty === 1 ? '' : 's'} (you have {f.upgrade.materialHave})
                  </span>
                  , for {exact(f.upgrade.netDailyAfter)} a day.
                </div>
                <Btn
                  v="gold"
                  size="sm"
                  disabled={busy || clean < f.upgrade.cost || f.upgrade.materialHave < f.upgrade.materialQty}
                  title={
                    clean < f.upgrade.cost
                      ? `Wants ${exact(f.upgrade.cost)} clean`
                      : f.upgrade.materialHave < f.upgrade.materialQty
                        ? `Wants ${f.upgrade.materialQty} ${f.upgrade.materialName}. Jobs drop them, and the Crusher makes them.`
                        : `Take ${f.name} to level ${f.upgrade.toLevel}`
                  }
                  onClick={() => void upgrade(f.slotId)}
                  style={{ flex: 'none' }}
                >
                  {clean < f.upgrade.cost ? 'Short on clean' : f.upgrade.materialHave < f.upgrade.materialQty ? 'Short on parts' : 'Upgrade'}
                </Btn>
              </Row>
            )}
          </div>
        )
      })}
    </>
  )
}

export function Market() {
  const { player, now } = useGame()
  const market = useSession((s) => s.market)
  const catalog = useSession((s) => s.catalog)
  const busy = useSession((s) => s.busy)
  const refusal = useSession((s) => s.refusal)
  const notice = useSession((s) => s.notice)
  const me = useSession((s) => s.me)
  const loadMarket = useSession((s) => s.loadMarket)
  const buyItem = useSession((s) => s.buyItem)
  const cancelListing = useSession((s) => s.cancelListing)
  const buyBlack = useSession((s) => s.buyBlack)
  const listItem = useSession((s) => s.listItem)

  const [mode, setMode] = useState<Mode>('player')
  // ALL, because the rail's first tab is All and it is now a real board. It opened on Weapons
  // because All was a lie, which meant a player pressing the tab they were already on got nothing.
  const [cat, setCat] = useState<Cat>('all')
  const [q, setQ] = useState('')
  const [rarity, setRarity] = useState('all')
  const [sort, setSort] = useState('price-asc')
  const [tab, setTab] = useState<'mine' | 'pockets' | 'kit' | 'fronts'>('mine')
  const [asking, setAsking] = useState<Record<string, string>>({})

  useEffect(() => {
    void loadMarket(cat)
  }, [cat, loadMarket])

  const listings = useMemo(() => {
    if (!market) return []
    const needle = q.trim().toLowerCase()
    const out = market.listings.filter((l) => {
      if (rarity !== 'all' && l.rarity !== rarity) return false
      if (needle && !`${l.name} ${l.sub} ${l.kind} ${l.seller}`.toLowerCase().includes(needle)) return false
      return true
    })
    const by: Record<string, (a: Listing, b: Listing) => number> = {
      'price-asc': (a, b) => a.price - b.price,
      'price-desc': (a, b) => b.price - a.price,
      newest: (a, b) => b.listedAt - a.listedAt,
      name: (a, b) => a.name.localeCompare(b.name),
    }
    return [...out].sort(by[sort] ?? by['price-asc'])
  }, [market, q, rarity, sort])

  const black = useMemo(() => {
    if (!market) return []
    const needle = q.trim().toLowerCase()
    return market.black.filter(
      (o) => !needle || `${o.name} ${o.sub}`.toLowerCase().includes(needle),
    )
  }, [market, q])

  /** What can go on the board: gear and fronts, never contraband. */
  const listable = useMemo(
    () => (market?.inventory ?? []).filter((i) => i.category !== 'contraband' && !i.equipped),
    [market],
  )

  const fee = market ? market.feeBp / 100 : 0
  const worn = wornBySlot(market?.inventory, catalog)

  const { stacked, touch } = useViewport()
  return (
    <Stack>
      {/* ------------------------------------------------------ shopping side */}
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        <Panel style={{ flex: 'none' }}>
          <Row gap={14} style={{ padding: '9px 12px 10px', alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 10 }}>
            <div>
              <Row gap={9} style={{ flexWrap: 'wrap' }}>
                <span className="t-display" style={{ fontSize: 27, color: 'var(--ink-hi)', letterSpacing: '.06em' }}>
                  Market
                </span>
                <span style={{ fontSize: 12, color: 'var(--ink-dim)', fontStyle: 'italic' }}>
                  Buy, sell, and move what moves power.
                </span>
              </Row>
              <Row gap={0} style={{ marginTop: 9 }}>
                {(
                  [
                    { id: 'player', label: 'The Fence' },
                    { id: 'black', label: 'Black Market' },
                  ] as { id: Mode; label: string }[]
                ).map((m) => {
                  const on = mode === m.id
                  return (
                    <button
                      key={m.id}
                      onClick={() => setMode(m.id)}
                      className="t-head"
                      style={{
                        padding: '8px 20px',
                        minHeight: touch ? 44 : undefined,
                        fontSize: 11.5,
                        letterSpacing: '.15em',
                        color: on ? 'var(--gold-hot)' : 'var(--ink-dim)',
                        background: on
                          ? 'linear-gradient(180deg, rgba(214,168,75,.18), rgba(214,168,75,.03))'
                          : 'rgba(255,255,255,.02)',
                        border: `1px solid ${on ? 'var(--line-brass-hot)' : 'var(--line)'}`,
                        borderRight: m.id === 'player' ? 'none' : undefined,
                        cursor: 'pointer',
                        transition: 'color 130ms ease, background 130ms ease',
                      }}
                    >
                      {m.label}
                    </button>
                  )
                })}
              </Row>
            </div>

            <Spacer />

            <Row
              gap={0}
              style={{
                alignSelf: 'stretch',
                flex: stacked ? '1 1 100%' : undefined,
                border: '1px solid var(--line)',
                background: 'linear-gradient(180deg, rgba(255,255,255,.03), transparent)',
              }}
            >
              {[
                { k: 'dirty' as const, label: 'Dirty Cash', v: money(player.dirty), c: 'var(--dirty)' },
                {
                  k: null,
                  label: 'Your Sale Fee',
                  v: `${fee.toFixed(1)}%`,
                  c: 'var(--ink-hi)',
                },
                { k: 'clean' as const, label: 'Clean Cash', v: money(player.clean), c: 'var(--clean)' },
              ].map((s, i) => (
                <div
                  key={s.label}
                  style={{
                    padding: stacked ? '8px 10px' : '10px 22px',
                    minWidth: stacked ? 0 : 150,
                    flex: stacked ? '1 1 0' : undefined,
                    borderLeft: i === 0 ? 'none' : '1px solid var(--line-soft)',
                  }}
                >
                  <Row gap={5}>
                    {s.k ? <Res k={s.k} s={11} /> : <Icon n="info" s={11} c="var(--ink-dim)" />}
                    <span className="t-label" style={{ fontSize: sz(8.5) }}>{s.label}</span>
                  </Row>
                  <div className="t-num" style={{ fontSize: 20, color: s.c, marginTop: 2 }}>
                    {s.v}
                  </div>
                  {s.k === null && (
                    <div style={{ fontSize: sz(9.5), color: 'var(--ink-faint)' }}>
                      {/* The fee is a function of YOUR Heat, not of the goods. It is the second
                          place Heat taxes you and the one people notice. */}
                      Rises with your Heat
                    </div>
                  )}
                </div>
              ))}
            </Row>
          </Row>
        </Panel>

        <CatRail cat={cat} setCat={setCat} counts={market?.counts ?? {}} stacked={stacked} />

        <Panel flat style={{ flex: 'none' }}>
          <Row gap={7} style={{ padding: '7px 8px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', width: stacked ? '100%' : 230, flex: 'none' }}>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search listings..."
                style={{
                  width: '100%',
                  padding: '7px 28px 7px 9px',
                  background: '#05080a',
                  border: '1px solid var(--line)',
                  color: 'var(--ink)',
                  font: '400 12px/1 var(--f-body)',
                  outline: 'none',
                }}
              />
              <Icon
                n="search"
                s={13}
                c="var(--ink-dim)"
                style={{ position: 'absolute', right: 8, top: 8, pointerEvents: 'none' }}
              />
            </div>
            <Select
              label="Rarity"
              value={rarity}
              onChange={setRarity}
              opts={[
                { v: 'all', l: 'All' },
                { v: 'common', l: 'Common' },
                { v: 'uncommon', l: 'Uncommon' },
                { v: 'rare', l: 'Rare' },
                { v: 'exceptional', l: 'Exceptional' },
                { v: 'epic', l: 'Epic' },
                { v: 'legendary', l: 'Legendary' },
              ]}
            />
            <Select
              label="Sort"
              value={sort}
              onChange={setSort}
              opts={[
                { v: 'price-asc', l: 'Price, Low to High' },
                { v: 'price-desc', l: 'Price, High to Low' },
                { v: 'newest', l: 'Newest' },
                { v: 'name', l: 'Name' },
              ]}
            />
            <Spacer />
            {/* On its own line: beside the selects it painted over them at 393px. */}
            {refusal && (
              <div className="t-label" style={{ flex: '1 1 100%', fontSize: sz(9.5), color: 'var(--arrest)', lineHeight: 1.4 }}>
                {refusal}
              </div>
            )}
            {!refusal && notice && (
              <div style={{ flex: '1 1 100%', fontSize: sz(11.5), color: 'var(--dirty)', lineHeight: 1.4 }}>
                {notice}
              </div>
            )}
          </Row>
        </Panel>

        <div className="scroll-y" style={{ flex: 1, paddingRight: 2 }}>
          {mode === 'player' ? (
            <>
              <Row gap={8} style={{ padding: '2px 2px 6px' }}>
                <span className="t-head" style={{ fontSize: 12, color: 'var(--ink-hi)', letterSpacing: '.19em' }}>
                  On the Board
                </span>
                <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-faint)' }}>
                  {listings.length} of {market?.total ?? 0}
                </span>
                <Spacer />
                <span className="t-label" style={{ fontSize: sz(8.5) }}>Escrowed, logged, clean cash only</span>
              </Row>

              {listings.length === 0 ? (
                <Panel flat style={{ padding: '26px 0', alignItems: 'center' }}>
                  <Icon n="crate" s={26} c="var(--ink-faint)" />
                  <div className="t-head" style={{ fontSize: 11, color: 'var(--ink-dim)', letterSpacing: '.2em', marginTop: 8 }}>
                    Nothing on the board
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-faint)', marginTop: 3 }}>
                    Loosen the filters, or put something up yourself.
                  </div>
                </Panel>
              ) : (
                <div
                  style={{
                    // Auto-fill rather than a fixed six. At 1920 the rail leaves about 218px per
                    // column across six, and this card does not fit in 218px: the price rendered
                    // as "$8,40" with the rest painted over by the next card.
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(236px, 1fr))',
                    gap: 8,
                    alignItems: 'stretch',
                  }}
                >
                  {listings.map((it) => (
                    <ListingCard
                      key={it.id}
                      it={it}
                      def={catalog.get(it.itemId)}
                      mine={it.sellerId === me?.playerId}
                      busy={busy}
                      worn={worn}
                      /*
                        THE ROW'S OWN CATEGORY, never the tab's. The book is sharded per category
                        and `all` is not a shard, so a buy from the merged board has to name the
                        aisle the listing actually lives in. `Listing.cat` has always carried it.
                      */
                      onBuy={() => void buyItem(it.cat, it.itemId, 1, it.price)}
                      onCancel={() => void cancelListing(it.cat, it.id)}
                    />
                  ))}
                </div>
              )}

              {/* What actually changed hands, which is the only honest price signal there is. */}
              <Row gap={8} style={{ padding: '14px 2px 6px' }}>
                <span className="t-head" style={{ fontSize: 12, color: 'var(--ink-hi)', letterSpacing: '.19em' }}>
                  Changed Hands
                </span>
                <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-faint)' }}>
                  {(market?.recent ?? []).length}
                </span>
                <Spacer />
                <span className="t-label" style={{ fontSize: sz(8.5) }}>What people actually paid</span>
              </Row>
              {(market?.recent ?? []).length === 0 ? (
                <Panel flat style={{ padding: '18px 0', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
                    Nothing has traded in this aisle yet.
                  </div>
                </Panel>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                  {(market?.recent ?? []).map((f) => {
                    const def = catalog.get(f.itemId)
                    return (
                      <Panel key={f.id} flat>
                        <Row gap={8} style={{ padding: '8px 9px' }}>
                          <Art
                            src={A(def?.artId ?? 'market.rarity.plate_common')}
                            alt={def?.name ?? f.itemId}
                            scrim="none"
                            style={{ width: 40, height: 40, flex: 'none', border: '1px solid var(--line-soft)' }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 11.5,
                                color: 'var(--ink-hi)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {def?.name ?? f.itemId}
                            </div>
                            <div className="t-label" style={{ fontSize: sz(8) }}>
                              {ago(f.at, now)} &middot; {f.qty} moved
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', flex: 'none' }}>
                            <div className="t-num" style={{ fontSize: 12.5, color: CUR[f.currency].c }}>
                              {money(f.unitPrice)}
                            </div>
                            <div className="t-label" style={{ fontSize: sz(8) }}>fee {money(f.fee)}</div>
                          </div>
                        </Row>
                      </Panel>
                    )
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <Row gap={8} style={{ padding: '2px 2px 6px' }}>
                <span className="t-head" style={{ fontSize: 12, color: 'var(--ink-hi)', letterSpacing: '.19em' }}>
                  What He Is Holding
                </span>
                <Icon n="clock" s={11} c="var(--brass-lit)" />
                <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-faint)' }}>
                  turns over in {clock(((market?.blackRefreshAt ?? now) - now) / 1000)}
                </span>
                <Spacer />
                <span className="t-label" style={{ fontSize: sz(8.5) }}>Cash on the table, no paper, over book</span>
              </Row>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: 8 }}>
                {black.map((o) => (
                  <BlackCard
                    key={o.itemId}
                    o={o}
                    def={catalog.get(o.itemId)}
                    worn={worn}
                    busy={busy}
                    onBuy={() => void buyBlack(o.category as MarketCategory, o.itemId, 1, o.price)}
                  />
                ))}
              </div>
            </>
          )}
          <div style={{ height: 4 }} />
        </div>
      </Col>

      {/* ---------------------------------------------------------- right rail */}
      <Rail width={336}>
        <Panel style={{ flex: 'none' }}>
          <PanelHead title="Market Insights" icon="ledger" sub="last 24 hours" />
          <div style={{ padding: '4px 10px 9px' }}>
            {(market?.insights ?? []).length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--ink-faint)', padding: '10px 0' }}>
                Nothing has traded in this aisle yet. Prices appear once somebody sells.
              </div>
            ) : (
              (market?.insights ?? []).map((s) => {
                const up = s.deltaBp >= 0
                const c = up ? 'var(--dirty)' : 'var(--heat)'
                return (
                  <Row
                    key={s.itemId}
                    gap={8}
                    style={{ padding: '5px 0', borderBottom: '1px solid var(--line-soft)' }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: 'var(--ink-hi)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {s.name}
                      </div>
                      <div className="t-label" style={{ fontSize: sz(8) }}>
                        {s.vol24h} moved &middot; vwap {money(s.vwap24h)}
                      </div>
                    </div>
                    <Row gap={3} style={{ flex: 'none', width: 52, justifyContent: 'flex-end' }}>
                      <Icon n="chev" s={10} c={c} style={{ transform: up ? 'rotate(-90deg)' : 'rotate(90deg)' }} />
                      <span className="t-mono" style={{ fontSize: 11, color: c }}>
                        {Math.abs(s.deltaBp / 100).toFixed(1)}%
                      </span>
                    </Row>
                    <div style={{ width: 104, flex: 'none' }}>
                      <Spark d={s.trend} c={c} h={24} />
                    </div>
                  </Row>
                )
              })
            )}
          </div>
        </Panel>

        <Panel style={{ flex: '1 1 0' }}>
          <PanelHead
            title="My Activity"
            icon="contracts"
            right={
              <Tabs
                items={[
                  { id: 'mine', label: `Listed (${market?.mine.length ?? 0})` },
                  { id: 'pockets', label: `Pockets (${listable.length})` },
                  { id: 'kit', label: 'Kit' },
                  { id: 'fronts', label: 'Fronts' },
                ]}
                value={tab}
                onChange={setTab}
              />
            }
          />
          <div className="scroll-y" style={{ flex: 1, padding: '4px 9px' }}>
            {tab === 'kit' && <KitTab />}
            {tab === 'fronts' && <FrontsTab />}
            {tab !== 'kit' && tab !== 'fronts' && tab === 'mine'
              ? (market?.mine ?? []).map((l) => (
                  <Row
                    key={l.id}
                    gap={8}
                    style={{ padding: '6px 2px', borderBottom: '1px solid var(--line-soft)' }}
                  >
                    <Art
                      src={A(l.artId)}
                      alt={l.name}
                      scrim="none"
                      style={{ width: 38, height: 32, flex: 'none', border: '1px solid var(--line-soft)' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: 'var(--ink-hi)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {l.name}
                      </div>
                      <Row gap={4}>
                        <Icon n="clock" s={9} c="var(--ink-faint)" />
                        <span className="t-mono" style={{ fontSize: sz(9.5), color: 'var(--ink-dim)' }}>
                          {clock((l.expiresAt - now) / 1000)} left
                        </span>
                      </Row>
                    </div>
                    <span className="t-num" style={{ fontSize: 12.5, color: CUR[l.cur].c, flex: 'none' }}>
                      {money(l.price)}
                    </span>
                    <Btn
                      size="sm"
                      v="ghost"
                      disabled={busy}
                      onClick={() => void cancelListing(l.cat, l.id)}
                    >
                      Pull
                    </Btn>
                  </Row>
                ))
              : listable.map((i) => (
                  <PocketRow
                    key={i.slotId}
                    item={i}
                    ask={asking[i.slotId] ?? ''}
                    setAsk={(v) => setAsking((a) => ({ ...a, [i.slotId]: v }))}
                    floor={catalog.get(i.itemId)?.priceFloor ?? 0}
                    feeBp={market?.feeBp ?? 0}
                    busy={busy}
                    onList={(price) => void listItem(i.category as MarketCategory, i.slotId, 1, price)}
                  />
                ))}
            {tab === 'pockets' && listable.length === 0 && (
              <div style={{ fontSize: 11, color: 'var(--ink-faint)', padding: '10px 2px' }}>
                Nothing to sell. Contraband goes to the fence, not the board.
              </div>
            )}
          </div>
          <div style={{ padding: '7px 9px 9px', borderTop: '1px solid var(--line-soft)' }}>
            <div className="t-label" style={{ fontSize: sz(8.5) }}>
              Listing needs your password again
            </div>
          </div>
        </Panel>
      </Rail>
    </Stack>
  )
}

/**
 * One thing in your pockets, with an asking price box.
 *
 * The floor and the fee are both server-published: the floor is 2x the item's Crusher value and
 * the fee is your own Heat-scaled fence fee. What the row computes is the NET, which is
 * arithmetic on two numbers the server sent, and that is the one thing a client is allowed to do.
 */
function PocketRow({
  item,
  ask,
  setAsk,
  floor,
  feeBp,
  busy,
  onList,
}: {
  item: InventoryItem
  ask: string
  setAsk: (v: string) => void
  floor: number
  feeBp: number
  busy: boolean
  onList: (price: Cents) => void
}) {
  const dollars = Number(ask)
  const price = Number.isFinite(dollars) && dollars > 0 ? Math.round(dollars * 100) : 0
  const under = price > 0 && price < floor
  const net = price > 0 ? price - Math.ceil((price * feeBp) / 10_000) : 0

  return (
    <div style={{ padding: '6px 2px', borderBottom: '1px solid var(--line-soft)' }}>
      <Row gap={8}>
        <Art
          src={A(item.artId)}
          alt={item.name}
          scrim="none"
          style={{ width: 38, height: 32, flex: 'none', border: '1px solid var(--line-soft)' }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--ink-hi)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.name}
          </div>
          <div className="t-label" style={{ fontSize: sz(8) }}>
            floor {money(floor as Cents)}
            {item.cond !== null ? ` · ${item.cond}%` : ''}
          </div>
        </div>
        <input
          value={ask}
          onChange={(e) => setAsk(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="ask"
          style={{
            width: 74,
            padding: '5px 6px',
            background: '#05080a',
            border: `1px solid ${under ? 'var(--arrest)' : 'var(--line)'}`,
            color: 'var(--ink)',
            font: '400 11px/1 var(--f-mono, var(--f-body))',
            outline: 'none',
            textAlign: 'right',
          }}
        />
        <Btn size="sm" v="police" disabled={busy || price <= 0 || under} onClick={() => onList(price as Cents)}>
          List
        </Btn>
      </Row>
      {price > 0 && (
        <Row gap={6} style={{ marginTop: 3, paddingLeft: 46 }}>
          <span className="t-label" style={{ fontSize: sz(8) }}>
            {under ? 'Under the crusher floor' : 'You keep'}
          </span>
          {!under && (
            <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--clean)' }}>
              {money(net as Cents)}
            </span>
          )}
        </Row>
      )}
    </div>
  )
}

export type { Rarity }
export { nth }
